import { createSelector } from '@ngrx/store';
import { CropAid } from 'src/app/crop-aid/crop-aid.model';
import { selectCropAids } from 'src/app/crop-aid/store/crop-aid.selectors';
import {
  SalesOrderPeriod,
  TreatmentType,
} from 'src/app/sales-order-period/models/sales-order-period.models';
import {
  selectPastSalesOrderPeriods,
  selectSalesOrderPeriods,
} from 'src/app/sales-order-period/store/sales-order-period.selectors';
import {
  CropData,
  CropFieldAdvice,
  ExecutedTreatmentWithNitrogenEffectivity,
  FertilizationSettingWithIds,
  NutrientBalanceItem,
  NutrientBalanceTable,
  TreatmentOrExecutedTreatmentWithNitrogenEffectivity,
  TreatmentWithNitrogenEffectivity,
} from 'src/app/shared/models';
import {
  selectCropFieldAdvices,
  selectCropFieldAdvicesByIds,
  selectCrops,
} from 'src/app/store/selectors';

import { selectExecutedTreatments, selectFertilizationSettings } from './selectors';
import { selectEditingTreatments } from './treatment.selectors';
import {
  AdvicedFertilizers,
  FertilizerAidAdvice,
  FertilizerData,
  fertilizerElements,
  FertilizerItem,
  FertilizerProps,
} from '../models/fertilization-result.model';

const multiplyNutrionalComposition = (
  nutritionalComposition: FertilizerData,
  multiplier: number,
): FertilizerData =>
  (Object.keys(nutritionalComposition) as (keyof FertilizerData)[]).reduce<FertilizerData>(
    (acc, curr) => ({
      ...acc,
      [curr]: nutritionalComposition[curr] * multiplier,
    }),
    {},
  );

export const selectAdvicedFertilizers = ({ treatments, areExecutedTreatments }: FertilizerProps) =>
  createSelector(
    selectEditingTreatments,
    selectCropAids,
    (advicedTreatments: TreatmentWithNitrogenEffectivity[], cropAids: CropAid[]) =>
      [
        ...treatments.filter(
          (treatment) => !treatment.id && treatment.isAnExecutedTreatment === areExecutedTreatments,
        ),
        ...advicedTreatments.filter(
          (treatment) => treatment.treatmentType === TreatmentType.Fertilization,
        ),
      ]
        .map((advicedTreatment) => {
          const foundTreatment = treatments.find(({ id }) => id === advicedTreatment.id);
          if (foundTreatment && foundTreatment.isAnExecutedTreatment === areExecutedTreatments) {
            return { ...foundTreatment, primary: true };
          }
          return { ...advicedTreatment, primary: false };
        })
        .reduce(
          (acc, curr) => [
            ...acc,
            ...curr.aidAdvices.map((aidAdvice) => ({
              ...aidAdvice,
              primary: !!curr.primary,
              isAnExecutedTreatment: curr.isAnExecutedTreatment,
              cropAid: cropAids.find(({ id }) => id === aidAdvice.cropAidId),
              periodId: curr.periodId,
              frequency: curr.frequency,
            })),
          ],
          [] as FertilizerAidAdvice[],
        )
        .filter(({ cropAid }) => !!cropAid && cropAid.isFertilizer)
        .map(
          ({
            cropAid,
            primary,
            dosage,
            isAnExecutedTreatment,
            nitrogenEffectivity,
            frequency,
          }) => ({
            source: cropAid.name,
            data: multiplyNutrionalComposition(
              {
                ...cropAid.fertilizer.nutritionalComposition,
                n: (cropAid.fertilizer.nutritionalComposition?.n ?? 0) * (nitrogenEffectivity / 100),
              },
              dosage * frequency,
            ),
            displayDosage: `${dosage ?? 0} ${cropAid.unitOfMeasurement}`,
            primary,
            isExecuted: isAnExecutedTreatment,
          }),
        ),
  );

const sumFertilizerData = (fertilizerData: FertilizerItem[], elements: (keyof FertilizerData)[]) =>
  fertilizerData.reduce((acc, curr) => {
    elements.forEach((element) => {
      acc[element] = (acc[element] || 0) + (curr.data[element] || 0);
    });
    return { ...acc };
  }, {} as FertilizerData);

const createNutrientBalanceItems = (supply: FertilizerData, drainage: FertilizerData) => {
  const nutrientBalanceItems: NutrientBalanceItem[] = [];

  nutrientBalanceItems.push({
    source: 'n',
    data: {
      supply: supply.n,
      drainage: drainage.n,
      balance: supply.n - drainage.n,
    },
  });

  nutrientBalanceItems.push({
    source: 'p2O5',
    data: {
      supply: supply.p2O5,
      drainage: drainage.p2O5,
      balance: supply.p2O5 - drainage.p2O5,
    },
  });

  nutrientBalanceItems.push({
    source: 'mgO',
    data: {
      supply: supply.mgO,
      drainage: drainage.mgO,
      balance: supply.mgO - drainage.mgO,
    },
  });

  nutrientBalanceItems.push({
    source: 'k2O',
    data: {
      supply: supply.k2O,
      drainage: drainage.k2O,
      balance: supply.k2O - drainage.k2O,
    },
  });

  return nutrientBalanceItems;
};

export const selectTableReportHistoricFertilizers = createSelector(
  selectExecutedTreatments,
  selectCropAids,
  (executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[] = [], cropAids: CropAid[]) => {
    const results: FertilizerItem[][] = [];
    let treatmentsByAdviceId: {
      adviceId: number;
      executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[];
    }[] = [];
    executedTreatments.forEach((treatment) => {
      if (!treatmentsByAdviceId[treatment.adviceId]) {
        treatmentsByAdviceId[treatment.adviceId] = {
          adviceId: treatment.adviceId,
          executedTreatments: [treatment],
        };
      } else {
        treatmentsByAdviceId[treatment.adviceId].executedTreatments.push(treatment);
      }
    });
    treatmentsByAdviceId = treatmentsByAdviceId.filter((el) => el != null);

    treatmentsByAdviceId.forEach((item) => {
      const result = selectAdvicedFertilizers({
        elements: fertilizerElements,
        areExecutedTreatments: true,
        treatments: [],
      }).projector(
        item.executedTreatments as unknown as TreatmentWithNitrogenEffectivity[],
        cropAids,
      ) as AdvicedFertilizers[];
      result.forEach((fertilizer) => {
        fertilizer.adviceId = item.adviceId;
      });
      results.push(result);
    });

    return results;
  },
);

export const selectTableReportAdvicedFertilizers = (
  adviceIds: number[],
  treatments?: TreatmentOrExecutedTreatmentWithNitrogenEffectivity,
) =>
  createSelector(
    selectCropFieldAdvicesByIds(adviceIds),
    selectSalesOrderPeriods,
    selectPastSalesOrderPeriods,
    selectCropAids,
    (
      cropFieldAdvices: CropFieldAdvice[],
      salesOrderPeriods: SalesOrderPeriod[],
      pastSalesOrderPeriods: SalesOrderPeriod[],
      cropAids: CropAid[],
    ): AdvicedFertilizers[][] => {
      const results: AdvicedFertilizers[][] = [];

      cropFieldAdvices.forEach((advice) => {
        if (advice) {
          const editingTreatments = selectEditingTreatments.projector(
            advice,
            TreatmentType.Fertilization,
            salesOrderPeriods,
            pastSalesOrderPeriods,
            cropAids,
          );
          const result = selectAdvicedFertilizers({
            elements: fertilizerElements,
            areExecutedTreatments: false,
            treatments: treatments ? [treatments as TreatmentWithNitrogenEffectivity] : [],
          }).projector(editingTreatments, cropAids) as AdvicedFertilizers[];
          result.forEach((fertilizer) => {
            fertilizer.adviceId = advice.adviceId;
          });
          results.push(result);
        }
      });

      return results;
    },
  );

export const selectNutrientBalanceReport = (adviceIds: number[]) =>
  createSelector(
    selectTableReportHistoricFertilizers,
    selectTableReportAdvicedFertilizers(adviceIds),
    selectCrops,
    selectCropFieldAdvices,
    selectFertilizationSettings,
    (
      historicFertilizers: AdvicedFertilizers[][],
      advicedFertilizers: AdvicedFertilizers[][],
      crops: CropData[],
      cropFieldAdvices: CropFieldAdvice[],
      fertilizationSettingWithIds: FertilizationSettingWithIds[] = [],
    ) => {
      let tables: NutrientBalanceTable[] = [];

      const totalsTable: NutrientBalanceTable = {
        name: 'total',
        translateName: true,
        totalHectares: 0,
        nutrientBalanceItems: [],
        metaData: { isTotals: true },
      };

      const totalSupply: FertilizerData = {
        n: 0,
        p2O5: 0,
        mgO: 0,
        k2O: 0,
      };

      const totalDrainage: FertilizerData = {
        n: 0,
        p2O5: 0,
        mgO: 0,
        k2O: 0,
      };

      const advices = adviceIds
        .map((adviceId) => cropFieldAdvices.find((a) => a.adviceId === adviceId))
        .filter((a) => a);

      for (const advice of advices) {
        const fertilizationItems: FertilizerItem[] = [];

        const table: NutrientBalanceTable = {
          name: `${advice.name} (${advice.crop.name})`,
          translateName: false,
          totalHectares: advice.areaInHectares,
          nutrientBalanceItems: [],
          metaData: {
            cropFieldName: advice.name,
            cropName: advice.crop.name,
            cropVarietyName: advice.cropVariety?.name,
          },
        };
        totalsTable.totalHectares += advice.areaInHectares;

        const filteredFertilizationSettings = fertilizationSettingWithIds.filter(
          (r) => r.adviceId === advice.adviceId,
        );
        const fertilizationSettings = !filteredFertilizationSettings?.length
          ? ({} as FertilizationSettingWithIds)
          : filteredFertilizationSettings[0];

        // Executed treatments
        historicFertilizers.forEach((adviceFertilizers) => {
          adviceFertilizers = adviceFertilizers.filter(
            (fertilizer) => fertilizer && fertilizer.adviceId === advice.adviceId,
          );
          fertilizationItems.push(...adviceFertilizers);
        });

        // Current/Advised treatments
        advicedFertilizers.forEach((adviceFertilizers) => {
          adviceFertilizers = adviceFertilizers.filter(
            (fertilizer) => fertilizer && fertilizer.adviceId === advice.adviceId,
          );
          fertilizationItems.push(...adviceFertilizers);
        });

        let adviceSupply: FertilizerData = {
          n: 0,
          p2O5: 0,
          mgO: 0,
          k2O: 0,
        };

        if (fertilizationItems.length > 0) {
          adviceSupply = sumFertilizerData(fertilizationItems, fertilizerElements);
        }

        totalSupply.n += adviceSupply.n * advice.areaInHectares;
        totalSupply.p2O5 += adviceSupply.p2O5 * advice.areaInHectares;
        totalSupply.mgO += adviceSupply.mgO * advice.areaInHectares;
        totalSupply.k2O += adviceSupply.k2O * advice.areaInHectares;

        // Harvested crop drainage
        const harvestedCropDrainageData: FertilizerData = {
          n: 0,
          p2O5: 0,
          mgO: 0,
          k2O: 0,
        };

        const adviceCrop = crops.find((crop) => crop.id === advice.crop.id);
        if (adviceCrop?.nutritionHarvestedCrop) {
          const expectedCropYield = fertilizationSettings?.expectedCropYield || 0;
          harvestedCropDrainageData.n =
            (adviceCrop.nutritionHarvestedCrop.n || 0) * expectedCropYield;
          harvestedCropDrainageData.p2O5 =
            (adviceCrop.nutritionHarvestedCrop.p2O5 || 0) * expectedCropYield;
          harvestedCropDrainageData.mgO =
            (adviceCrop.nutritionHarvestedCrop.mgO || 0) * expectedCropYield;
          harvestedCropDrainageData.k2O =
            (adviceCrop.nutritionHarvestedCrop.k2O || 0) * expectedCropYield;
        }

        totalDrainage.n += harvestedCropDrainageData.n * advice.areaInHectares;
        totalDrainage.p2O5 += harvestedCropDrainageData.p2O5 * advice.areaInHectares;
        totalDrainage.mgO += harvestedCropDrainageData.mgO * advice.areaInHectares;
        totalDrainage.k2O += harvestedCropDrainageData.k2O * advice.areaInHectares;

        const nutrientBalanceItems = createNutrientBalanceItems(
          adviceSupply,
          harvestedCropDrainageData,
        );
        table.nutrientBalanceItems.push(...nutrientBalanceItems);

        tables.push(table);
      }

      totalsTable.nutrientBalanceItems = createNutrientBalanceItems(totalSupply, totalDrainage);
      tables = [totalsTable, ...tables];
      return tables;
    },
  );
